import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { X, Plus, Sparkles, CheckCircle2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { ApplePayButton } from "@/components/site/ApplePayButton";
import { GooglePayButton } from "@/components/site/GooglePayButton";
import { getErrorMessage } from "@/lib/errors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRICE = 5;

const PaymentDialog = ({ open, onClose, request, config }) => {
  const { t } = useLang();
  const [paid, setPaid] = useState(false);

  const canPaypal = config?.paypal_configured && config?.paypal_client_id && request;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-espresso border-gold/30 text-sand max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial italic text-3xl text-sand">
            {paid ? t.pay.paid : t.pay.title}
          </DialogTitle>
          <DialogDescription className="text-sand/60 font-light">
            {paid ? "" : t.pay.desc}
          </DialogDescription>
        </DialogHeader>

        {request && (
          <div className="rounded-xl border border-gold/20 bg-ivy/40 p-4 flex items-center justify-between">
            <span className="text-sand/70 text-sm">{t.form.total}</span>
            <span className="font-display text-gold text-3xl">€ {request.amount?.toFixed(2)}</span>
          </div>
        )}

        {paid ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="text-gold" size={40} />
            <p className="text-sand/80 font-light">{t.form.success}</p>
          </div>
        ) : canPaypal ? (
          <div className="mt-2 space-y-2" data-testid="paypal-container">
            <PayPalScriptProvider
              options={{
                clientId: config.paypal_client_id,
                currency: "EUR",
                components: "buttons,applepay,googlepay",
                "enable-funding": "paylater,card",
              }}
            >
              <ApplePayButton
                request={request}
                onPaid={() => {
                  setPaid(true);
                  toast.success(t.pay.paid);
                }}
                onError={() => toast.error(t.form.error)}
              />
              <GooglePayButton
                request={request}
                onPaid={() => {
                  setPaid(true);
                  toast.success(t.pay.paid);
                }}
                onError={() => toast.error(t.form.error)}
              />
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill" }}
                createOrder={async () => {
                  const { data } = await axios.post(`${API}/paypal/create-order`, { request_id: request.id });
                  return data.id;
                }}
                onApprove={async (data) => {
                  await axios.post(`${API}/paypal/capture/${data.orderID}`);
                  setPaid(true);
                  toast.success(t.pay.paid);
                }}
                onError={() => toast.error(t.form.error)}
              />
            </PayPalScriptProvider>
          </div>
        ) : (
          <div className="mt-2 rounded-xl border border-gold/20 bg-ivy/40 p-5">
            <p className="text-sand/75 text-sm leading-relaxed font-light">{t.pay.manual}</p>
          </div>
        )}

        <Button
          onClick={onClose}
          variant="ghost"
          className="mt-2 text-sand/60 hover:text-gold hover:bg-transparent"
          data-testid="payment-close-button"
        >
          {t.pay.close}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const BookingForm = ({ selectedPackage = "personal" }) => {
  const { t, lang } = useLang();
  const [pkg, setPkg] = useState(selectedPackage);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [birth, setBirth] = useState("");
  const [context, setContext] = useState("");
  const [recipient, setRecipient] = useState("");
  const [questions, setQuestions] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [request, setRequest] = useState(null);
  const [annualStatus, setAnnualStatus] = useState(null);
  const [code, setCode] = useState("");

  useEffect(() => setPkg(selectedPackage), [selectedPackage]);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  // Annual subscription status (recognized by email + personal code)
  useEffect(() => {
    if (pkg !== "annual" || !email || !email.includes("@")) {
      setAnnualStatus(null);
      return;
    }
    const id = setTimeout(() => {
      axios
        .get(`${API}/annual-status`, { params: { email, code } })
        .then((r) => setAnnualStatus(r.data))
        .catch(() => setAnnualStatus(null));
    }, 500);
    return () => clearTimeout(id);
  }, [pkg, email, code]);

  const validCount = useMemo(() => questions.filter((q) => q.trim()).length, [questions]);
  const total = useMemo(() => {
    if (pkg === "annual") {
      if (annualStatus?.active) return 0;
      if (annualStatus?.is_subscriber) return 0; // existing subscriber, code required
      return 160;
    }
    if (pkg === "gift") return 15;
    return Math.max(validCount, 3) * PRICE;
  }, [pkg, validCount, annualStatus]);

  const addQuestion = () => setQuestions((q) => [...q, ""]);
  const removeQuestion = (i) => setQuestions((q) => q.filter((_, idx) => idx !== i));
  const updateQuestion = (i, v) => setQuestions((q) => q.map((x, idx) => (idx === i ? v : x)));

  const submit = async (e) => {
    e.preventDefault();
    const valid = questions.map((q) => q.trim()).filter(Boolean);
    if (pkg === "personal" && valid.length < 3) {
      toast.error(t.form.minErr);
      return;
    }
    if (valid.length < 1) {
      toast.error(t.form.minErr);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/reading-requests`, {
        name,
        email,
        birth_date: birth || null,
        context: context.trim() || null,
        questions: valid,
        package: pkg,
        recipient_name: pkg === "gift" ? recipient : null,
        code: pkg === "annual" ? code.trim() : null,
        lang,
      });
      setRequest(data);
      if (data.status === "covered") {
        // Covered by active annual subscription — no payment needed
        toast.success(t.form.coveredSuccess);
        setQuestions(["", "", ""]);
        setContext("");
        axios.get(`${API}/annual-status`, { params: { email, code } }).then((r) => setAnnualStatus(r.data)).catch(() => {});
      } else {
        toast.success(t.form.success);
        setPayOpen(true);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, t.form.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="relative py-28 sm:py-36" data-testid="booking-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-start">
        {/* Left copy */}
        <Reveal>
          <Eyebrow>{t.form.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display font-light text-sand text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            {t.form.title}
          </h2>
          <p className="mt-6 text-sand/70 text-base sm:text-lg leading-relaxed font-light max-w-md">
            {t.form.intro}
          </p>
          {/* package chips */}
          <div className="mt-8 flex flex-wrap gap-3">
            {t.offers.cards.map((c) => (
              <button
                key={c.id}
                onClick={() => setPkg(c.id)}
                className={`rounded-full px-4 py-2 text-sm font-body tracking-wide border transition-colors ${
                  pkg === c.id
                    ? "border-gold bg-gold/15 text-gold"
                    : "border-gold/25 text-sand/70 hover:border-gold/50"
                }`}
                data-testid={`package-chip-${c.id}`}
              >
                {c.glyph} {c.t}
              </button>
            ))}
          </div>
          {pkg === "annual" && (
            <div
              className={`mt-6 rounded-2xl border p-5 ${
                annualStatus?.active ? "border-moss/50 bg-moss/15" : "border-gold/25 bg-espresso/40"
              }`}
              data-testid="annual-status-box"
            >
              {annualStatus?.active ? (
                <>
                  <p className="text-gold font-serifluxe uppercase text-xs tracking-mystic">{t.form.annualActive}</p>
                  <p className="mt-2 text-sand text-lg font-light">
                    <span className="font-display text-gold text-3xl">{annualStatus.remaining}</span>
                    <span className="text-sand/60"> / {annualStatus.total}</span>
                  </p>
                  <p className="text-sand/60 text-sm">
                    {lang === "nl" ? "vragen over — geen betaling nodig" : lang === "en" ? "questions left — no payment needed" : "domande rimaste — nessun pagamento necessario"}
                  </p>
                </>
              ) : annualStatus?.is_subscriber ? (
                <>
                  <p className="text-gold font-serifluxe uppercase text-xs tracking-mystic">{t.form.codeLabel}</p>
                  <p className="mt-2 text-sand/70 text-sm font-light">{t.form.codePrompt}</p>
                  <Input
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={t.form.codePlaceholder}
                    className="mt-3 h-11 bg-ivy/60 border-gold/25 text-sand tracking-[0.3em] font-display text-lg placeholder:text-sand/30 placeholder:tracking-normal placeholder:font-body placeholder:text-sm focus-visible:ring-gold"
                    data-testid="form-code-input"
                  />
                  {code.length === 6 && annualStatus?.requires_code && (
                    <p className="mt-2 text-rust text-xs" data-testid="form-code-error">{t.form.codeErr}</p>
                  )}
                </>
              ) : (
                <p className="text-sand/70 text-sm font-light">{t.form.annualNew}</p>
              )}
            </div>
          )}
          <div className="mt-6 rounded-2xl border border-gold/20 bg-espresso/40 p-6 flex items-center gap-4">
            <Sparkles className="text-gold shrink-0" size={22} />
            <p className="text-sand/70 text-sm font-light leading-relaxed">{t.hero.badge} · PayPal · Google&nbsp;Pay · Apple&nbsp;Pay</p>
          </div>
        </Reveal>

        {/* Right form */}
        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            className="rounded-2xl border border-gold/25 bg-espresso/50 p-6 sm:p-8 backdrop-blur-sm"
            data-testid="booking-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sand/70 text-xs uppercase tracking-widest">{t.form.name}</Label>
                <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t.form.namePh} className="mt-2 h-11 bg-ivy/60 border-gold/25 text-sand placeholder:text-sand/30 focus-visible:ring-gold" data-testid="form-name-input" />
              </div>
              <div>
                <Label className="text-sand/70 text-xs uppercase tracking-widest">{t.form.email}</Label>
                <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.form.emailPh} className="mt-2 h-11 bg-ivy/60 border-gold/25 text-sand placeholder:text-sand/30 focus-visible:ring-gold" data-testid="form-email-input" />
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sand/70 text-xs uppercase tracking-widest">{t.form.birth}</Label>
                <Input type="date" value={birth} onChange={(e) => setBirth(e.target.value)} className="mt-2 h-11 bg-ivy/60 border-gold/25 text-sand focus-visible:ring-gold" data-testid="form-birth-input" />
              </div>
              {pkg === "gift" && (
                <div>
                  <Label className="text-sand/70 text-xs uppercase tracking-widest">{t.form.recipient}</Label>
                  <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} className="mt-2 h-11 bg-ivy/60 border-gold/25 text-sand focus-visible:ring-gold" data-testid="form-recipient-input" />
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t border-gold/15" data-testid="context-section">
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <h3 className="font-display text-sand text-2xl font-light">
                  {t.form.contextSectionTitle}{" "}
                  <span className="text-gold/60 text-sm font-body italic tracking-wide">({t.form.contextSectionOptional})</span>
                </h3>
              </div>
              <p className="text-sand/60 text-sm leading-relaxed font-light mb-4">
                {t.form.contextSectionIntro}
              </p>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value.slice(0, 2000))}
                maxLength={2000}
                rows={4}
                placeholder={t.form.contextPh}
                className="bg-ivy/60 border-gold/25 text-sand placeholder:text-sand/30 focus-visible:ring-gold resize-none leading-relaxed"
                data-testid="form-context-input"
              />
              <div className="mt-1.5 flex justify-end">
                <span className="text-sand/40 text-xs tabular-nums" data-testid="form-context-count">{context.length}/2000</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gold/15" data-testid="questions-section">
              <h3 className="font-display text-sand text-2xl font-light mb-2">
                {t.form.questionsSectionTitle}
              </h3>
              <p className="text-sand/60 text-sm leading-relaxed font-light mb-4">
                {t.form.questionsSectionIntro}
              </p>
            </div>

            <div className="mt-4 space-y-3">
              <AnimatePresence initial={false}>
                {questions.map((q, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                  >
                    <Label className="text-sand/60 text-xs uppercase tracking-widest">{t.form.question} {i + 1}</Label>
                    <div className="mt-2 flex gap-2">
                      <Textarea
                        value={q}
                        onChange={(e) => updateQuestion(i, e.target.value)}
                        rows={2}
                        className="bg-ivy/60 border-gold/25 text-sand focus-visible:ring-gold resize-none"
                        data-testid={`form-question-${i}`}
                      />
                      {questions.length > 1 && (
                        <button type="button" onClick={() => removeQuestion(i)} className="text-sand/40 hover:text-rust transition-colors self-start mt-1" data-testid={`form-remove-question-${i}`}>
                          <X size={18} />
                        </button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <button type="button" onClick={addQuestion} className="mt-4 inline-flex items-center gap-2 text-gold hover:text-cream transition-colors text-sm font-body tracking-wide" data-testid="form-add-question">
              <Plus size={16} /> {t.form.addQ}
            </button>

            <div className="mt-7 flex items-center justify-between border-t border-gold/15 pt-6">
              <div>
                <p className="text-sand/50 text-xs uppercase tracking-widest">{t.form.total}</p>
                <p className="font-display text-gold text-4xl" data-testid="form-total">€ {total.toFixed(2)}</p>
                {pkg === "personal" && (
                  <p className="text-sand/40 text-xs mt-1">{Math.max(validCount, 3)} {t.form.questionsCount}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-full bg-rust hover:bg-rust/90 text-cream h-12 px-8 font-body tracking-wide transition-transform hover:scale-[0.97]"
                data-testid="form-submit-button"
              >
                {loading ? t.form.submitting : t.form.submit}
              </Button>
            </div>
            <p className="mt-6 text-sand/40 text-xs leading-relaxed">{t.form.note}</p>
          </form>
        </Reveal>
      </div>

      <PaymentDialog open={payOpen} onClose={() => setPayOpen(false)} request={request} config={config} />
    </section>
  );
};

export default BookingForm;
